import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { useAuthStore } from '../stores/authStore';
import { useGameStore } from '../stores/gameStore';
import { API_BASE_URL } from '../api/client';
import type { GameAction } from '../types';

class StompClient {
  private client: Client | null = null;
  private isConnected = false;

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      const { token } = useAuthStore.getState();
      
      this.client = new Client({
        webSocketFactory: () => new SockJS(`${API_BASE_URL}/ws`),
        connectHeaders: {
          Authorization: `Bearer ${token}`,
        },
        debug: (str) => {
          console.log('STOMP Debug:', str);
        },
        onConnect: () => {
          console.log('Connected to WebSocket');
          this.isConnected = true;
          useGameStore.getState().setConnected(true);
          resolve();
        },
        onStompError: (frame) => {
          console.error('STOMP Error:', frame);
          this.isConnected = false;
          useGameStore.getState().setConnected(false);
          reject(new Error(frame.headers.message));
        },
        onWebSocketError: (error) => {
          console.error('WebSocket Error:', error);
          this.isConnected = false;
          useGameStore.getState().setConnected(false);
          reject(error);
        },
      });

      this.client.activate();
    });
  }

  disconnect() {
    if (this.client && this.isConnected) {
      this.client.deactivate();
      this.isConnected = false;
      useGameStore.getState().setConnected(false);
    }
  }

  subscribeToLobby(callback: (message: any) => void) {
    if (!this.client || !this.isConnected) {
      throw new Error('Not connected to WebSocket');
    }

    return this.client.subscribe('/topic/lobby', callback);
  }

  subscribeToGame(matchId: number, callback: (message: any) => void) {
    if (!this.client || !this.isConnected) {
      throw new Error('Not connected to WebSocket');
    }

    return this.client.subscribe(`/topic/game.${matchId}`, callback);
  }

  subscribeToAlerts(callback: (message: any) => void) {
    if (!this.client || !this.isConnected) {
      throw new Error('Not connected to WebSocket');
    }

    return this.client.subscribe('/queue/alerts', callback);
  }

  sendGameAction(action: GameAction) {
    if (!this.client || !this.isConnected) {
      throw new Error('Not connected to WebSocket');
    }

    this.client.publish({
      destination: '/app/game.play',
      body: JSON.stringify(action),
    });
  }

  sendLobbyAction(action: GameAction) {
    if (!this.client || !this.isConnected) {
      throw new Error('Not connected to WebSocket');
    }

    this.client.publish({
      destination: '/app/lobby.join',
      body: JSON.stringify(action),
    });
  }

  isConnectedToWebSocket(): boolean {
    return this.isConnected;
  }
}

export const stompClient = new StompClient();
